import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Encabezado, Pestanas } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cajon,
  Campo,
  Cargando,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
  clases,
} from '../comun/componentes';
import { useAccion, useDatos, useRetraso } from '../comun/useDatos';
import { enviar } from '../api/cliente';
import { fecha, fechaYHora, hoyIso, legible, sumarDias } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import type {
  AlertaInventario,
  CicloVista,
  DosimetriaVista,
  EquipoRadiologicoVista,
  HabilitacionVista,
  InsumoVista,
} from '../api/tipos';

const SECCIONES = [
  { valor: 'inventario', rotulo: 'Inventario' },
  { valor: 'bioseguridad', rotulo: 'Bioseguridad' },
  { valor: 'cumplimiento', rotulo: 'Cumplimiento' },
];

/** Clínica: lo que hace falta para que el consultorio funcione y pase una inspección. */
export function Clinica() {
  const { seccion = 'inventario' } = useParams();
  const navegar = useNavigate();

  return (
    <>
      <Encabezado
        titulo="Clínica"
        detalle="Insumos, esterilización y los papeles que mira una inspección."
      />

      <Pestanas
        actual={seccion}
        opciones={SECCIONES}
        alCambiar={(valor) => navegar(`/clinica/${valor}`)}
      />

      {seccion === 'inventario' && <Inventario />}
      {seccion === 'bioseguridad' && <Bioseguridad />}
      {seccion === 'cumplimiento' && <Cumplimiento />}
    </>
  );
}

function Inventario() {
  const { puede } = useSesion();
  const [texto, setTexto] = useState('');
  const buscado = useRetraso(texto);
  const [consumiendo, setConsumiendo] = useState<InsumoVista | null>(null);

  const insumos = useDatos<InsumoVista[]>(
    `/api/v1/inventario${buscado.trim() ? `?q=${encodeURIComponent(buscado.trim())}` : ''}`,
    [buscado],
  );
  const alertas = useDatos<AlertaInventario[]>('/api/v1/inventario/alertas');

  return (
    <div className="space-y-4">
      {alertas.datos && alertas.datos.length > 0 && (
        <Panel titulo="Hay que reponer o revisar" descripcion={`${alertas.datos.length} alertas`}>
          <ul className="space-y-2">
            {alertas.datos.map((alerta, indice) => (
              <li key={indice} className="flex flex-wrap items-center justify-between gap-2">
                <span className="min-w-0">
                  <span className="block text-sm text-tinta-800">{alerta.insumo}</span>
                  <span className="block text-dato text-tinta-500">{alerta.detalle}</span>
                </span>
                <Insignia tono={alerta.motivo.toLowerCase().includes('vencid') ? 'alerta' : 'aviso'}>
                  {legible(alerta.motivo)}
                </Insignia>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel titulo="Insumos">
        <input
          type="search"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscá por nombre, código o categoría…"
          className="campo mb-3"
          aria-label="Buscar insumo"
        />

        {insumos.cargando && <Cargando que="el inventario" />}
        {insumos.error && <ErrorEnPantalla mensaje={insumos.error} reintentar={insumos.recargar} />}
        {insumos.datos?.length === 0 && (
          <Vacio titulo="Sin insumos" detalle="Cargá el primero para llevar el stock y sus vencimientos." />
        )}

        {insumos.datos && insumos.datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Insumo</th>
                  <th scope="col">Categoría</th>
                  <th scope="col" className="text-right">Existencia</th>
                  <th scope="col" className="text-right">Mínimo</th>
                  <th scope="col">Lotes</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {insumos.datos.map((insumo) => (
                  <tr key={insumo.id} className={clases(insumo.bajoMinimo && 'bg-aviso-50/50')}>
                    <td>
                      <span className="block font-medium text-tinta-900">{insumo.nombre}</span>
                      <span className="cifra block text-dato text-tinta-400">{insumo.codigo}</span>
                    </td>
                    <td className="text-dato text-tinta-500">{insumo.categoria ?? '—'}</td>
                    <td className="cifra text-right font-medium">
                      {insumo.existencia} {legible(insumo.unidad).toLowerCase()}
                    </td>
                    <td className="cifra text-right text-tinta-500">{insumo.stockMinimo}</td>
                    <td>
                      <span className="flex flex-wrap gap-1">
                        {insumo.bajoMinimo && <Insignia tono="aviso">bajo mínimo</Insignia>}
                        {insumo.lotesVencidos > 0 && (
                          <Insignia tono="alerta">{insumo.lotesVencidos} vencidos</Insignia>
                        )}
                        {insumo.lotesPorVencer > 0 && (
                          <Insignia tono="aviso">{insumo.lotesPorVencer} por vencer</Insignia>
                        )}
                      </span>
                    </td>
                    <td className="text-right">
                      {puede('inventario.editar') && (
                        <Boton onClick={() => setConsumiendo(insumo)}>Movimiento</Boton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <MovimientoDeStock
        insumo={consumiendo}
        alCerrar={() => setConsumiendo(null)}
        alGuardar={() => {
          setConsumiendo(null);
          insumos.recargar();
          alertas.recargar();
        }}
      />
    </div>
  );
}

function MovimientoDeStock({
  insumo,
  alCerrar,
  alGuardar,
}: {
  insumo: InsumoVista | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [tipo, setTipo] = useState<'ingreso' | 'consumo'>('consumo');
  const [cantidad, setCantidad] = useState('1');
  const [lote, setLote] = useState('');
  const [vencimiento, setVencimiento] = useState('');
  const [concepto, setConcepto] = useState('');
  const [trazabilidad, setTrazabilidad] = useState<{ lote: string; cantidad: number }[] | null>(null);

  const guardar = async () => {
    if (!insumo) return;

    if (tipo === 'ingreso') {
      const hecho = await correr(() =>
        enviar(`/api/v1/inventario/${insumo.id}/ingresos`, {
          insumoId: insumo.id,
          cantidad: Number(cantidad),
          loteCodigo: lote.trim(),
          vencimiento: vencimiento || null,
          concepto: concepto.trim() || 'Compra',
        }),
      );

      if (hecho !== null) {
        alGuardar();
      }

      return;
    }

    const consumido = await correr(() =>
      enviar<{ lote: string; cantidad: number }[]>(`/api/v1/inventario/${insumo.id}/consumos`, {
        insumoId: insumo.id,
        cantidad: Number(cantidad),
        concepto: concepto.trim() || 'Uso en sesión',
      }),
    );

    if (consumido) {
      setTrazabilidad(consumido);
    }
  };

  return (
    <Cajon
      abierto={insumo != null}
      titulo={insumo?.nombre ?? ''}
      alCerrar={() => {
        setTrazabilidad(null);
        alCerrar();
      }}
      pie={
        trazabilidad ? (
          <Boton
            variante="primario"
            ancho
            onClick={() => {
              setTrazabilidad(null);
              alGuardar();
            }}
          >
            Listo
          </Boton>
        ) : (
          <div className="flex gap-2">
            <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void guardar()}>
              {tipo === 'ingreso' ? 'Registrar ingreso' : 'Registrar consumo'}
            </Boton>
            <Boton onClick={alCerrar}>Cancelar</Boton>
          </div>
        )
      }
    >
      {trazabilidad ? (
        <div className="space-y-3">
          <Aviso tono="exito">Consumo registrado. Se descontó del lote que vence primero.</Aviso>
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Lote</th>
                  <th scope="col" className="text-right">Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {trazabilidad.map((linea) => (
                  <tr key={linea.lote}>
                    <td className="cifra">{linea.lote}</td>
                    <td className="cifra text-right">{linea.cantidad}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        </div>
      ) : (
        <div className="space-y-4">
          <div role="radiogroup" aria-label="Tipo" className="flex gap-1 rounded-lg bg-papel-200 p-1">
            {(['consumo', 'ingreso'] as const).map((opcion) => (
              <button
                key={opcion}
                type="button"
                role="radio"
                aria-checked={tipo === opcion}
                onClick={() => setTipo(opcion)}
                className={clases(
                  'min-h-tactil flex-1 rounded-md px-3 text-sm font-medium',
                  tipo === opcion ? 'bg-white text-marca-700 shadow-panel' : 'text-tinta-600',
                )}
              >
                {opcion === 'consumo' ? 'Consumo' : 'Ingreso'}
              </button>
            ))}
          </div>

          <Campo
            etiqueta="Cantidad"
            inputMode="numeric"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value.replace(/\D/g, ''))}
          />

          {tipo === 'ingreso' && (
            <>
              <Campo etiqueta="Lote" value={lote} onChange={(e) => setLote(e.target.value)} />
              <Campo
                etiqueta="Vencimiento"
                type="date"
                value={vencimiento}
                onChange={(e) => setVencimiento(e.target.value)}
              />
            </>
          )}

          <Campo
            etiqueta="Concepto"
            value={concepto}
            placeholder={tipo === 'ingreso' ? 'Compra' : 'Uso en sesión'}
            onChange={(e) => setConcepto(e.target.value)}
          />

          {tipo === 'consumo' && (
            <p className="text-dato text-tinta-500">
              El consumo sale del lote que vence primero, y la respuesta dice de cuáles salió.
            </p>
          )}

          {error && (
            <Aviso tono="alerta">{error}</Aviso>
          )}
        </div>
      )}
    </Cajon>
  );
}

function Bioseguridad() {
  const { puede } = useSesion();
  const [desde, setDesde] = useState(sumarDias(hoyIso(), -14));
  const [hasta, setHasta] = useState(hoyIso());
  const [abriendo, setAbriendo] = useState(false);
  const [cerrando, setCerrando] = useState<CicloVista | null>(null);

  const ciclos = useDatos<CicloVista[]>(
    `/api/v1/bioseguridad/ciclos?desde=${desde}&hasta=${hasta}`,
    [desde, hasta],
  );

  const noConformes = (ciclos.datos ?? []).filter((c) => c.finalizadoEn && !c.esUtilizable);

  return (
    <div className="space-y-4">
      {noConformes.length > 0 && (
        <Aviso tono="alerta">
          {noConformes.length === 1
            ? 'Hay un ciclo no conforme: su instrumental no se puede usar.'
            : `Hay ${noConformes.length} ciclos no conformes: ese instrumental no se puede usar.`}
        </Aviso>
      )}

      <Panel
        titulo="Bitácora de esterilización"
        descripcion="Lista para imprimir ante una inspección"
        accion={
          puede('cumplimiento.editar') && (
            <Boton variante="primario" onClick={() => setAbriendo(true)}>
              Abrir ciclo
            </Boton>
          )
        }
      >
        <div className="mb-3 grid gap-3 tablet:grid-cols-2">
          <Campo etiqueta="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          <Campo etiqueta="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>

        {ciclos.cargando && <Cargando que="la bitácora" />}
        {ciclos.datos?.length === 0 && (
          <Vacio titulo="Sin ciclos en el período" detalle="Abrí uno cuando cargues el autoclave." />
        )}

        {ciclos.datos && ciclos.datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Ciclo</th>
                  <th scope="col">Carga</th>
                  <th scope="col">Inicio</th>
                  <th scope="col">Químico</th>
                  <th scope="col">Biológico</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {ciclos.datos.map((ciclo) => (
                  <tr key={ciclo.id}>
                    <td>
                      <span className="cifra block font-medium">{ciclo.numeroCiclo}</span>
                      <span className="block text-dato text-tinta-400">{ciclo.equipo}</span>
                    </td>
                    <td className="text-dato">{ciclo.carga}</td>
                    <td className="cifra whitespace-nowrap text-dato">{fechaYHora(ciclo.iniciadoEn)}</td>
                    <td>
                      <ResultadoControl valor={ciclo.controlQuimico} />
                    </td>
                    <td>
                      <ResultadoControl valor={ciclo.controlBiologico} />
                    </td>
                    <td className="text-right">
                      {!ciclo.finalizadoEn && puede('cumplimiento.editar') && (
                        <Boton onClick={() => setCerrando(ciclo)}>Cerrar</Boton>
                      )}
                      {ciclo.finalizadoEn && (
                        <Insignia tono={ciclo.esUtilizable ? 'exito' : 'alerta'}>
                          {ciclo.esUtilizable ? 'Utilizable' : 'No usar'}
                        </Insignia>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <AbrirCiclo
        abierto={abriendo}
        alCerrar={() => setAbriendo(false)}
        alAbrir={() => {
          setAbriendo(false);
          ciclos.recargar();
        }}
      />

      <CerrarCiclo
        ciclo={cerrando}
        alCerrar={() => setCerrando(null)}
        alGuardar={() => {
          setCerrando(null);
          ciclos.recargar();
        }}
      />
    </div>
  );
}

function ResultadoControl({ valor }: { valor: string }) {
  return (
    <Insignia
      tono={valor === 'Conforme' ? 'exito' : valor === 'NoConforme' ? 'alerta' : 'neutro'}
    >
      {legible(valor)}
    </Insignia>
  );
}

function AbrirCiclo({
  abierto,
  alCerrar,
  alAbrir,
}: {
  abierto: boolean;
  alCerrar: () => void;
  alAbrir: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [equipo, setEquipo] = useState('Autoclave');
  const [numero, setNumero] = useState('');
  const [carga, setCarga] = useState('');

  const abrir = async () => {
    const hecho = await correr(() =>
      enviar('/api/v1/bioseguridad/ciclos', {
        equipo: equipo.trim(),
        numeroCiclo: numero.trim(),
        carga: carga.trim(),
      }),
    );

    if (hecho !== null) {
      setNumero('');
      setCarga('');
      alAbrir();
    }
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Abrir ciclo de esterilización"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void abrir()}>
            Abrir ciclo
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <Campo etiqueta="Equipo" value={equipo} onChange={(e) => setEquipo(e.target.value)} />
        <Campo
          etiqueta="Número de ciclo"
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
          ayuda="El que imprime el autoclave y se pega en el paquete."
        />
        <Campo
          etiqueta="Carga"
          value={carga}
          placeholder="Cajas de operatoria y espejos"
          onChange={(e) => setCarga(e.target.value)}
        />

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function CerrarCiclo({
  ciclo,
  alCerrar,
  alGuardar,
}: {
  ciclo: CicloVista | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [temperatura, setTemperatura] = useState('134');
  const [duracion, setDuracion] = useState('45');
  const [control, setControl] = useState('Conforme');

  const cerrar = async () => {
    if (!ciclo) return;

    const hecho = await correr(() =>
      enviar(`/api/v1/bioseguridad/ciclos/${ciclo.id}/finalizar`, {
        cicloId: ciclo.id,
        temperaturaMaximaC: Number(temperatura),
        duracionMin: Number(duracion),
        controlQuimico: control,
      }),
    );

    if (hecho !== null) {
      alGuardar();
    }
  };

  return (
    <Cajon
      abierto={ciclo != null}
      titulo={`Cerrar ciclo ${ciclo?.numeroCiclo ?? ''}`}
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void cerrar()}>
            Cerrar ciclo
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <Campo
          etiqueta="Temperatura máxima (°C)"
          inputMode="decimal"
          value={temperatura}
          onChange={(e) => setTemperatura(e.target.value)}
        />
        <Campo
          etiqueta="Duración (min)"
          inputMode="numeric"
          value={duracion}
          onChange={(e) => setDuracion(e.target.value.replace(/\D/g, ''))}
        />
        <Selector
          etiqueta="Control químico"
          value={control}
          onChange={(e) => setControl(e.target.value)}
        >
          <option value="Conforme">Conforme</option>
          <option value="NoConforme">No conforme</option>
        </Selector>

        <p className="text-dato text-tinta-500">
          Si el control da no conforme, el instrumental de este ciclo queda marcado como no
          utilizable.
        </p>

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function Cumplimiento() {
  const habilitaciones = useDatos<HabilitacionVista[]>('/api/v1/cumplimiento/habilitaciones');
  const equipos = useDatos<EquipoRadiologicoVista[]>('/api/v1/cumplimiento/equipos');
  const dosimetria = useDatos<DosimetriaVista[]>('/api/v1/cumplimiento/dosimetria');

  return (
    <div className="space-y-4">
      <Panel titulo="Habilitaciones y licencias" descripcion="Ordenadas por lo que vence antes">
        {habilitaciones.cargando && <Cargando que="las habilitaciones" />}
        {habilitaciones.error && (
          <ErrorEnPantalla mensaje={habilitaciones.error} reintentar={habilitaciones.recargar} />
        )}
        {habilitaciones.datos?.length === 0 && (
          <Vacio
            titulo="Sin habilitaciones cargadas"
            detalle="Cargá la del establecimiento y las matrículas de cada profesional."
          />
        )}

        {habilitaciones.datos && habilitaciones.datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Papel</th>
                  <th scope="col">Número</th>
                  <th scope="col">Emisor</th>
                  <th scope="col">Vence</th>
                  <th scope="col">Estado</th>
                </tr>
              </thead>
              <tbody>
                {habilitaciones.datos.map((papel) => (
                  <tr
                    key={papel.id}
                    className={clases(
                      papel.estado === 'Vencida' && 'bg-alerta-50/60',
                      papel.estado === 'PorVencer' && 'bg-aviso-50/50',
                    )}
                  >
                    <td>
                      <span className="block font-medium text-tinta-900">{papel.titulo}</span>
                      <span className="block text-dato text-tinta-400">{legible(papel.tipo)}</span>
                    </td>
                    <td className="cifra">{papel.numero}</td>
                    <td className="text-dato text-tinta-500">{papel.entidadEmisora ?? '—'}</td>
                    <td className="cifra whitespace-nowrap">{fecha(papel.venceEl)}</td>
                    <td>
                      <Insignia
                        tono={
                          papel.estado === 'Vencida'
                            ? 'alerta'
                            : papel.estado === 'PorVencer'
                              ? 'aviso'
                              : 'exito'
                        }
                      >
                        {papel.estado === 'PorVencer'
                          ? `en ${papel.diasParaVencer} días`
                          : papel.estado === 'Vencida'
                            ? `hace ${Math.abs(papel.diasParaVencer ?? 0)} días`
                            : 'Vigente'}
                      </Insignia>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <div className="grid gap-4 portatil:grid-cols-2">
        <Panel titulo="Equipos de rayos" descripcion="Con su licencia ARRN">
          {equipos.datos?.length === 0 && (
            <Vacio titulo="Sin equipos" detalle="Registrá el equipo para seguir su licencia." />
          )}
          {equipos.datos && equipos.datos.length > 0 && (
            <ul className="space-y-3">
              {equipos.datos.map((equipo) => (
                <li key={equipo.id} className="rounded-lg border border-papel-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-tinta-900">{equipo.nombre}</p>
                      <p className="text-dato text-tinta-500">
                        {[equipo.marca, equipo.modelo, equipo.ubicacion].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {equipo.estadoLicencia && (
                      <Insignia tono={equipo.estadoLicencia === 'Vigente' ? 'exito' : 'aviso'}>
                        {legible(equipo.estadoLicencia)}
                      </Insignia>
                    )}
                  </div>
                  {equipo.licenciaNumero && (
                    <p className="cifra mt-1 text-dato text-tinta-500">
                      Licencia {equipo.licenciaNumero} · vence {fecha(equipo.licenciaVence)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel titulo="Dosimetría" descripcion="Personal expuesto, lectura mensual">
          {dosimetria.datos?.length === 0 && (
            <Vacio titulo="Sin lecturas" detalle="Cargá la lectura del dosímetro cada mes." />
          )}
          {dosimetria.datos && dosimetria.datos.length > 0 && (
            <Desplazable>
              <table className="tabla">
                <thead>
                  <tr>
                    <th scope="col">Trabajador</th>
                    <th scope="col">Período</th>
                    <th scope="col" className="text-right">mSv</th>
                    <th scope="col" className="text-right">Año</th>
                  </tr>
                </thead>
                <tbody>
                  {dosimetria.datos.slice(0, 12).map((lectura) => (
                    <tr key={lectura.id}>
                      <td className="text-dato">{lectura.trabajador}</td>
                      <td className="cifra text-dato">{lectura.periodo.slice(0, 7)}</td>
                      <td className="cifra text-right">
                        <Insignia
                          tono={
                            lectura.resultado === 'Excedida'
                              ? 'alerta'
                              : lectura.resultado === 'Elevada'
                                ? 'aviso'
                                : 'exito'
                          }
                        >
                          {lectura.dosisMsv}
                        </Insignia>
                      </td>
                      <td className="cifra text-right text-tinta-500">{lectura.acumuladoAnualMsv}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Desplazable>
          )}
        </Panel>
      </div>
    </div>
  );
}
